/**
 * GraphQL fetcher for TanStack Query
 * Used with @graphql-codegen/typescript-react-query plugin
 */
import { print } from "graphql";
import type { DocumentNode } from "graphql";
import { getStorefrontApiUrl } from "@/modules/shopify/shopify";

const SHOPIFY_STOREFRONT_URL = getStorefrontApiUrl();
const SHOPIFY_STOREFRONT_TOKEN = import.meta.env
  .VITE_STOREFRONT_API_PUBLIC_ACCESS_TOKEN;

/**
 * Generic GraphQL fetcher function for TanStack Query hooks
 * Accepts both string queries and DocumentNode from gql tag
 */
export function fetcher<TData, TVariables extends Record<string, unknown>>(
  query: string | string | DocumentNode,
  variables?: TVariables,
  options?: RequestInit["headers"]
): () => Promise<TData> {
  return async () => {
    const additionalHeaders: Record<string, string> = {};

    // Convert HeadersInit to Record<string, string>
    if (options) {
      if (Array.isArray(options)) {
        options.forEach(([key, value]) => {
          additionalHeaders[key] = value;
        });
      } else if (options instanceof Headers) {
        options.forEach((value, key) => {
          additionalHeaders[key] = value;
        });
      } else {
        Object.assign(additionalHeaders, options);
      }
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN || "",
      ...additionalHeaders,
    };

    // Normalize boxed String instances used by graphql-codegen TypedDocumentString.
    const queryString =
      typeof query === "string" || query instanceof String
        ? query.toString()
        : print(query);

    const response = await fetch(SHOPIFY_STOREFRONT_URL || "", {
      method: "POST",
      headers,
      body: JSON.stringify({
        query: queryString,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`GraphQL request failed: ${response.statusText}`);
    }

    const json = await response.json();

    if (json.errors) {
      const errorMessages = json.errors
        .map((e: { message: string }) => e.message)
        .join("\n");
      throw new Error(`GraphQL errors:\n${errorMessages}`);
    }

    return json.data;
  };
}
